import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { MsalProvider } from '@azure/msal-react';
import { EventType, type EventMessage, type AuthenticationResult } from '@azure/msal-browser';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import App from './App';
import { msalInstance } from './auth/msalConfig';
import { tokenRef } from './auth/tokenRef';
import { registerSW } from 'virtual:pwa-register';
import { cleanupForeignServiceWorkerState } from './pwa-cleanup';
// Self-hosted design fonts (Pack B) — no runtime Google Fonts.
import '@fontsource-variable/bricolage-grotesque/index.css';
import '@fontsource-variable/public-sans/index.css';
import '@fontsource/ibm-plex-mono/400.css';
import '@fontsource/ibm-plex-mono/500.css';
import './index.css';

// Register the SW through the virtual module instead of the plugin's injected
// one-liner: in autoUpdate mode this also reloads the page when a new service
// worker takes control. Without it, a tab left open across a deploy keeps
// running the old bundle, whose hashed assets (e.g. the pdf.js worker) no
// longer exist on the server — the classic "Couldn't render PDF inline" after
// a redeploy.
registerSW({ immediate: true });

// Purge foreign SW caches/registrations left by the July 2026 cross-deploy
// incident. Fire-and-forget; see pwa-cleanup.ts.
void cleanupForeignServiceWorkerState();

// Push new access tokens into tokenRef as soon as MSAL acquires them.
msalInstance.addEventCallback((event: EventMessage) => {
  if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
    const payload = event.payload as AuthenticationResult;
    if (payload.account) msalInstance.setActiveAccount(payload.account);
    if (payload.accessToken) tokenRef.current = payload.accessToken;
  }
  if (event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS && event.payload) {
    const payload = event.payload as AuthenticationResult;
    if (payload.accessToken) tokenRef.current = payload.accessToken;
  }
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        // Don't retry on 401 — the axios interceptor is already redirecting
        // to login. Retrying just stacks more 401s in the network panel.
        const status = (error as { response?: { status?: number } })?.response?.status;
        if (status === 401) return false;
        return failureCount < 1;
      }
    },
    mutations: {
      retry: false
    }
  }
});

const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('Root element not found');

msalInstance.initialize().then(() => {
  // Handle any outstanding redirect promise so acquireTokenRedirect / loginRedirect
  // completes before the app renders.
  return msalInstance.handleRedirectPromise();
}).then(() => {
  createRoot(rootElement).render(
    <StrictMode>
      <MsalProvider instance={msalInstance}>
        <QueryClientProvider client={queryClient}>
          <App />
        </QueryClientProvider>
      </MsalProvider>
    </StrictMode>
  );
});
